
  class CartDiscountComponent extends HTMLElement {
    constructor() {
      super();
      this.activeFetch = null;
      this.isLoading = false;
    }

    connectedCallback() {
      this.setupEventListeners();
      this.hideAppliedSectionIfEmpty();
    }

    /**
     * Setup all event listeners for discount operations
     */
    setupEventListeners() {
      // Form submission for manual discount input
      const form = this.querySelector('.wt-cart__discount__form');
      if (form) {
        form.addEventListener('submit', (e) => this.applyDiscount(e));
      }

      // Remove discount pill click
      this.addEventListener('click', (e) => {
        if (e.target.closest('.wt-cart__discount__pill-remove')) {
          this.removeDiscount(e);
        }
      });

      // Popup trigger button
      const popupTrigger = document.querySelector('.wt-discount-popup-trigger');
      if (popupTrigger) {
        popupTrigger.addEventListener('click', () => this.openDiscountPopup());
      }

      // Popup close button
      const popupClose = document.querySelector('#wt-discount-popup-close');
      if (popupClose) {
        popupClose.addEventListener('click', () => this.closeDiscountPopup());
      }

      // Close popup when clicking overlay
      const overlay = document.querySelector('#wt-discount-popup-overlay');
      if (overlay) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            this.closeDiscountPopup();
          }
        });
      }
    }

    /**
     * Get all available discounts from section data attribute
     */
    getAllDiscounts() {
      try {
        // Get discounts from data attribute
        const discountsData = this.getAttribute('data-discounts');
        
        // If no discounts or empty string, return empty array
        if (!discountsData || discountsData.trim() === '') {
          console.log('No discounts configured');
          return [];
        }

        // Split by pipe character to get individual discount strings
        const discountStrings = discountsData.split('|');
        const discounts = [];

        discountStrings.forEach((discountString) => {
          if (discountString && discountString.trim() !== '') {
            const [code, ...descriptionParts] = discountString.split(':');
            const description = descriptionParts.join(':'); // In case description contains colons
            
            if (code && description) {
              discounts.push({
                code: code.trim(),
                description: description.trim(),
                type: 'manual'
              });
            }
          }
        });

        console.log('Parsed discounts:', discounts);
        return discounts;
      } catch (error) {
        console.error('Error parsing discount data:', error);
        return [];
      }
    }

    /**
     * Open the discount popup modal
     */
    openDiscountPopup() {
      const overlay = document.querySelector('#wt-discount-popup-overlay');
      if (overlay) {
        overlay.classList.remove('hidden');
        this.renderDiscountList();
      }
    }

    /**
     * Close the discount popup modal
     */
    closeDiscountPopup() {
      const overlay = document.querySelector('#wt-discount-popup-overlay');
      if (overlay) {
        overlay.classList.add('hidden');
      }
    }

    /**
     * Render the list of discounts in the popup
     */
    renderDiscountList() {
      const discountList = document.querySelector('#wt-discount-list');
      if (!discountList) return;

      const appliedDiscounts = this.getExistingDiscounts();
      const discounts = this.getAllDiscounts();

      if (discounts.length === 0) {
        discountList.innerHTML = '<div class="wt-discount-loading">No discounts available</div>';
        return;
      }

      discountList.innerHTML = discounts.map(discount => {
        const isApplied = appliedDiscounts.includes(discount.code.toUpperCase());
        const isAutoApplied = this.isAutoAppliedDiscount(discount.code);
        const isDisabled = isAutoApplied;

        return `
          <div class="wt-discount-item ${isDisabled ? 'disabled' : ''}" data-discount-code="${discount.code}">
            <div class="wt-discount-item-left">
              <div class="wt-discount-item-code">${this.escapeHtml(discount.code)}</div>
              <div class="wt-discount-item-description">${this.escapeHtml(discount.description)}</div>
              ${isAutoApplied ? '<div class="wt-discount-item-applied">✓ Automatically Applied</div>' : ''}
            </div>
            <button 
              type="button" 
              class="wt-discount-item-button" 
              ${isDisabled ? 'disabled' : ''}
              aria-label="${isApplied ? 'Applied' : 'Apply'} discount ${this.escapeHtml(discount.code)}"
            >
              ${isApplied ? 'Applied' : 'Apply'}
            </button>
          </div>
        `;
      }).join('');

      // Add click handlers to non-disabled items
      discountList.querySelectorAll('.wt-discount-item:not(.disabled)').forEach(item => {
        item.addEventListener('click', (e) => {
          const button = item.querySelector('.wt-discount-item-button');
          if (!button.disabled) {
            this.applyDiscountFromPopup(item.dataset.discountCode);
          }
        });
      });
    }

    /**
     * Check if a discount is auto-applied by Shopify admin
     */
    isAutoAppliedDiscount(code) {
      const cartLevelDiscounts = window.cartData?.cart_level_discount_applications || [];
      return cartLevelDiscounts.some(discount => 
        discount.type === 'automatic' && 
        discount.title?.toUpperCase() === code.toUpperCase()
      );
    }

    /**
     * Apply discount from popup modal
     */
    applyDiscountFromPopup(code) {
      const input = this.querySelector('input[name="discount"]');
      if (input) {
        input.value = code;
        const form = this.querySelector('.wt-cart__discount__form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true }));
        }
        this.closeDiscountPopup();
      }
    }

    /**
     * Hide applied discounts section if there are no discounts
     */
    hideAppliedSectionIfEmpty() {
      const appliedSection = this.querySelector('#wt-discount-applied');
      const pillsList = this.querySelector('.wt-cart__discount__pills');
      
      if (appliedSection && (!pillsList || pillsList.children.length === 0)) {
        appliedSection.classList.add('hidden');
      }
    }

    /**
     * Create and manage abort controller for fetch requests
     */
    createAbortController() {
      if (this.activeFetch) {
        this.activeFetch.abort();
      }
      const abortController = new AbortController();
      this.activeFetch = abortController;
      return abortController;
    }

    /**
     * Get all currently applied discount codes
     */
    getExistingDiscounts() {
      const discountCodes = [];
      const pills = this.querySelectorAll('.wt-cart__discount__pill');
      pills.forEach((pill) => {
        const code = pill.dataset.discountCode;
        if (code) discountCodes.push(code.toUpperCase());
      });
      return discountCodes;
    }

    /**
     * Show error message with specific type
     */
    showError(type) {
      const errorContainer = this.querySelector('[ref="cartDiscountError"]');
      const discountCodeError = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
      const shippingError = this.querySelector('[ref="cartDiscountErrorShipping"]');

      if (!errorContainer) return;
      errorContainer.classList.remove('hidden');

      if (type === 'discount_code') {
        discountCodeError?.classList.remove('hidden');
        shippingError?.classList.add('hidden');
      } else if (type === 'shipping') {
        shippingError?.classList.remove('hidden');
        discountCodeError?.classList.add('hidden');
      }
    }

    /**
     * Hide all error messages
     */
    hideError() {
      const errorContainer = this.querySelector('[ref="cartDiscountError"]');
      const discountCodeError = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
      const shippingError = this.querySelector('[ref="cartDiscountErrorShipping"]');

      errorContainer?.classList.add('hidden');
      discountCodeError?.classList.add('hidden');
      shippingError?.classList.add('hidden');
    }

    /**
     * Set button loading state
     */
    setButtonLoading(loading) {
      const button = this.querySelector('.wt-cart__discount__button');
      const input = this.querySelector('.wt-cart__discount__input');
      
      if (button) {
        button.disabled = loading;
        button.textContent = loading ? 'Applying...' : 'Apply';
      }
      if (input) {
        input.disabled = loading;
      }
    }

    /**
     * Apply discount code from input field
     */
    async applyDiscount(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isLoading) return;

      const input = this.querySelector('input[name="discount"]');
      if (!input) return;

      const discountCode = input.value.trim().toUpperCase();
      if (!discountCode) return;

      const existingDiscounts = this.getExistingDiscounts();

      if (existingDiscounts.includes(discountCode)) {
        input.value = '';
        this.hideError();
        return;
      }

      this.isLoading = true;
      this.setButtonLoading(true);
      const abortController = this.createAbortController();

      try {
        this.hideError();
        const discountString = [...existingDiscounts, discountCode].join(',');

        const response = await fetch(window.Shopify.routes.root + 'cart/update.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ discount: discountString }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          this.showError('discount_code');
          input.value = '';
          return;
        }

        const data = await response.json();

        const isApplicable = data.discount_codes?.some(
          (dc) => dc.code.toUpperCase() === discountCode && dc.applicable === true
        );

        if (!isApplicable) {
          input.value = '';
          const codeExists = data.discount_codes?.some(
            (dc) => dc.code.toUpperCase() === discountCode
          );
          this.showError(codeExists ? 'shipping' : 'discount_code');
          return;
        }

        this.addDiscountPill(discountCode);
        input.value = '';

        await this.updateCartDrawerInstantly(data);

        document.dispatchEvent(
          new CustomEvent('discount-applied', {
            detail: { code: discountCode, cart: data },
            bubbles: true,
          })
        );

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Discount error:', error);
          this.showError('discount_code');
        }
      } finally {
        this.isLoading = false;
        this.setButtonLoading(false);
        this.activeFetch = null;
      }
    }

    /**
     * Remove applied discount code
     */
    async removeDiscount(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isLoading) return;

      const button = event.target.closest('.wt-cart__discount__pill-remove');
      if (!button) return;

      const pill = button.closest('.wt-cart__discount__pill');
      if (!pill) return;

      const discountCode = pill.dataset.discountCode;
      if (!discountCode) return;

      const existingDiscounts = this.getExistingDiscounts();
      const newDiscounts = existingDiscounts.filter(code => code !== discountCode.toUpperCase());

      this.isLoading = true;
      const abortController = this.createAbortController();

      try {
        const discountString = newDiscounts.length > 0 ? newDiscounts.join(',') : '';

        const response = await fetch(window.Shopify.routes.root + 'cart/update.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ discount: discountString }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          console.error('Failed to remove discount');
          return;
        }

        const data = await response.json();

        pill.remove();

        const appliedSection = this.querySelector('#wt-discount-applied');
        const pillsList = this.querySelector('.wt-cart__discount__pills');
        
        if (appliedSection && (!pillsList || pillsList.children.length === 0)) {
          appliedSection.classList.add('hidden');
        }

        await this.updateCartDrawerInstantly(data);

        document.dispatchEvent(
          new CustomEvent('discount-removed', {
            detail: { code: discountCode },
            bubbles: true,
          })
        );

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Remove discount error:', error);
        }
      } finally {
        this.isLoading = false;
        this.activeFetch = null;
      }
    }

    /**
     * Add discount pill to UI
     */
    addDiscountPill(code) {
      const pillsList = this.querySelector('.wt-cart__discount__pills');
      const appliedSection = this.querySelector('#wt-discount-applied');

      if (!pillsList) return;

      if (appliedSection) {
        appliedSection.classList.remove('hidden');
      }

      const li = document.createElement('li');
      li.className = 'wt-cart__discount__pill';
      li.dataset.discountCode = code;
      li.innerHTML = `
        <span class="wt-cart__discount__pill-text">${this.escapeHtml(code)}</span>
        <button type="button" class="wt-cart__discount__pill-remove" aria-label="Remove discount ${this.escapeHtml(code)}">✕</button>
      `;

      pillsList.appendChild(li);
    }

    /**
     * Update cart drawer with fresh cart data
     */
    async updateCartDrawerInstantly(cartData) {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.json', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        const freshCartData = await cartResponse.json();

        this.updateLineItemsWithDiscounts(freshCartData.items);

        const itemCount = cartData.item_count;
        const cartHeader = document.querySelector('#CartDrawer-TotalQty');
        if (cartHeader) {
          const title = 'Your cart';
          cartHeader.textContent = itemCount > 0 ? `${title}\n            (${itemCount})` : title;
        }

        const subtotalValue = document.querySelector('.wt-cart__subtotal__value');
        if (subtotalValue && cartData.total_price) {
          const price = (cartData.total_price / 100).toFixed(2);
          const currency = this.getCurrency();
          subtotalValue.textContent = `${currency} ${price}`;
        }

        const freeShippingBar = document.querySelector('.wt-free-shipping-bar');
        if (freeShippingBar && cartData.total_price) {
          const thresholdPrice = 50000;
          const percentage = Math.min((cartData.total_price / thresholdPrice) * 100, 100);
          const progressBar = freeShippingBar.querySelector('.wt-progress-bar__fill');
          if (progressBar) {
            progressBar.style.width = percentage + '%';
          }
        }

        document.dispatchEvent(
          new CustomEvent('cart-drawer-updated', {
            detail: { cart: cartData },
            bubbles: true,
          })
        );

      } catch (error) {
        console.error('Error updating cart drawer:', error);
      }
    }

    /**
     * Update line items with discount information
     */
    updateLineItemsWithDiscounts(cartItems) {
      const cartItemsContainer = document.querySelector('#CartDrawer-CartItems');
      if (!cartItemsContainer) return;

      const cartItemElements = cartItemsContainer.querySelectorAll('.cart-item');

      cartItemElements.forEach((itemElement, index) => {
        if (cartItems[index]) {
          const cartItem = cartItems[index];
          
          const discountDisplay = itemElement.querySelector('.wt-cart__item-discount');
          
          if (cartItem.line_level_discount_allocations && 
              cartItem.line_level_discount_allocations.length > 0) {
            
            if (discountDisplay) {
              discountDisplay.style.display = 'flex';
              discountDisplay.style.alignItems = 'center';
              discountDisplay.style.gap = '8px';
            }

            const discountTexts = cartItem.line_level_discount_allocations.map(allocation => {
              const discountAmount = (allocation.amount / 100).toFixed(2);
              return `${allocation.discount_application.title} - Rs. ${discountAmount}`;
            });

            if (discountDisplay) {
              const svg = discountDisplay.querySelector('svg');
              const textNodes = Array.from(discountDisplay.childNodes).filter(
                node => node.nodeType === 3 && node.textContent.trim()
              );
              textNodes.forEach(node => node.remove());
              
              const textSpan = document.createElement('span');
              textSpan.textContent = discountTexts.join(', ');
              if (svg) {
                discountDisplay.appendChild(textSpan);
              }
            }
          } else {
            if (discountDisplay) {
              discountDisplay.style.display = 'none';
            }
          }

          const priceWrapper = itemElement.querySelector('.wt-cart__item__price-wrapper');
          if (priceWrapper && cartItem.original_line_price && cartItem.line_price) {
            const originalPrice = (cartItem.original_line_price / 100).toFixed(2);
            const salePrice = (cartItem.line_price / 100).toFixed(2);
            const currency = this.getCurrency();

            const oldPriceElement = priceWrapper.querySelector('.cart-item__old-price');
            const salePriceElement = priceWrapper.querySelector('.cart-item__discounted-prices .price:last-of-type');

            if (oldPriceElement) {
              oldPriceElement.textContent = `${currency} ${originalPrice}`;
            }

            if (salePriceElement) {
              salePriceElement.textContent = `${currency} ${salePrice}`;
            }
          }
        }
      });
    }

    /**
     * Get active currency
     */
    getCurrency() {
      return window.Shopify?.currency?.active || 'Rs.';
    }

    /**
     * Escape HTML special characters
     */
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };
      return text.replace(/[&<>"']/g, (m) => map[m]);
    }
  }

  // Register the custom element
  if (!customElements.get('cart-discount-component')) {
    customElements.define('cart-discount-component', CartDiscountComponent);
  }
